// frontend/src/pages/admin/AdminReviews.js
import React, { useState, useEffect } from 'react';
import { reviewsApi } from '../../api/reviews';
import { FaStar, FaCheck, FaTimes, FaFilter, FaSpinner, FaQuoteLeft, FaSync } from 'react-icons/fa';

function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const data = await reviewsApi.getAllReviewsAdmin(filterStatus === 'all' ? null : filterStatus);
      setReviews(data);
    } catch (err) {
      console.error("Failed to load reviews", err);
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (id, status) => {
    let reason = null;
    if (status === 'rejected') {
        reason = prompt("Enter rejection reason (optional):");
    }
    
    if (!window.confirm(`Are you sure you want to ${status.toUpperCase()} this review?`)) return;

    setProcessingId(id);
    try {
      await reviewsApi.moderateReview(id, status, reason);
      // Remove from list if we are filtering by specific status
      if (filterStatus !== 'all') {
          setReviews(prev => prev.filter(r => r.id !== id));
      } else {
          // Update status in place
          setReviews(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      }
    } catch (err) {
      alert("Action failed.");
    } finally {
      setProcessingId(null);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex text-yellow-400 text-xs">
        {[...Array(5)].map((_, i) => (
          <FaStar key={i} className={i < rating ? "text-yellow-400" : "text-gray-300"} />
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FaStar className="text-yellow-500" /> Review Moderation
        </h1>
        <button 
            onClick={fetchReviews} 
            className="text-gray-600 hover:text-primary flex items-center gap-2 text-sm"
        >
            <FaSync className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* FILTERS */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex items-center gap-4 overflow-x-auto">
        <FaFilter className="text-gray-400" />
        <div className="flex gap-2">
            {['pending', 'approved', 'rejected', 'all'].map(status => (
                <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-4 py-2 rounded-full text-sm font-bold capitalize transition-colors ${
                        filterStatus === status 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    {status}
                </button>
            ))}
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <div className="text-center py-10"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading...</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-500 text-lg">No {filterStatus} reviews found.</p>
            <p className="text-gray-400 text-sm">New reviews submitted by customers will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {reviews.map(review => (
            <div key={review.id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row gap-6">
              
              {/* Review Content */}
              <div className="flex-grow">
                <div className="flex items-center gap-3 mb-2">
                    <span className="font-bold text-gray-800">{review.user_name}</span>
                    <span className="text-xs text-gray-500">{new Date(review.created_at).toLocaleString()}</span>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded font-bold ${
                        review.status === 'approved' ? 'bg-green-100 text-green-800' :
                        review.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                    }`}>
                        {review.status}
                    </span>
                </div>
                
                <div className="mb-3">
                    {renderStars(review.rating)}
                </div>

                <div className="relative pl-6">
                    <FaQuoteLeft className="absolute left-0 top-0 text-gray-200 text-xl" />
                    <p className="text-gray-700 italic">{review.comment}</p>
                </div>
                
                <div className="mt-3 text-xs text-gray-500 font-mono">
                    Part ID: {review.part_id}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-row md:flex-col gap-2 justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-6 min-w-[120px]">
                {review.status === 'pending' && (
                    <>
                        <button 
                            onClick={() => handleModerate(review.id, 'approved')}
                            disabled={processingId === review.id}
                            className="bg-green-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-green-700 flex items-center gap-2 w-full justify-center"
                        >
                            <FaCheck /> Approve
                        </button>
                        <button 
                            onClick={() => handleModerate(review.id, 'rejected')}
                            disabled={processingId === review.id}
                            className="bg-red-100 text-red-600 px-4 py-2 rounded text-sm font-bold hover:bg-red-200 flex items-center gap-2 w-full justify-center"
                        >
                            <FaTimes /> Reject
                        </button>
                    </>
                )}
                {review.status === 'approved' && (
                    <button 
                        onClick={() => handleModerate(review.id, 'rejected')}
                        disabled={processingId === review.id}
                        className="bg-red-50 text-red-600 px-4 py-2 rounded text-sm font-bold hover:bg-red-100 flex items-center gap-2 w-full justify-center"
                    >
                        <FaTimes /> Revoke
                    </button>
                )}
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminReviews;